import { MDXEditor } from '@mdxeditor/editor';
import { Box } from '@mui/material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { useColorScheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import FileUploader from '@/components/FileUploader';
import {
  CATEGORY_ID,
  getAttachmentUploaderProps,
  isKanatiAttachmentAllowed,
  isKanatiCategory,
  KANATI_ADVISORY_DISCLAIMER,
  KANATI_CATEGORY_INFO_MESSAGES,
  KANATI_MAX_TEXT_LENGTH,
  KANATI_TEXT_TOO_LONG_MESSAGE,
  LOGS_ACTION_ID
} from '@/features/TicketCreateDialog/TicketCreateDialog.constants';
import { useFetchSettings } from '@/pages/Administration/Administration.store';
import {
  organizationsAtom,
  selectedOrganizationAtom
} from '@/pages/Organizations';
import { MDX_EDITOR_CONFIG } from '@/pages/Ticket/TicketCreate/TicketCreate.constants';
import { isLayoutMobileAtom } from '@/store/layout';
import { userAtom } from '@/store/user.store';
import useStyles from '../TicketCreateDialog.styles';
import {
  ServiceFormCategoryField,
  ServiceFormSubCategoryField
} from './fields';

const ServiceForm = () => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [organizations] = useAtom(organizationsAtom);
  const [userData] = useAtom(userAtom);
  const [{ id: selectedOrgId }] = useAtom(selectedOrganizationAtom);
  const { mode: themeMode } = useColorScheme();
  const {
    clearErrors,
    control,
    getValues,
    formState: { errors },
    setValue
  } = useFormContext();
  const { classes } = useStyles({ errors });

  const formOrgId = useWatch({ control, name: 'organization_id' });
  const orgLabel = useMemo(
    () => organizations.find((org) => org.id === formOrgId)?.label,
    [organizations, formOrgId]
  );
  const { data: settings } = useFetchSettings(orgLabel);

  const [logsAction, summary, ticketCategory] = useWatch({
    control,
    name: ['custom_data.logs_data.action', 'summary', 'ticket_category']
  });
  const isKanati = isKanatiCategory(ticketCategory);
  // the length limit only applies to external users; internal users are exempt
  const isKanatiLimited = isKanati && userData?.internal !== true;
  const isSummaryTooLong =
    isKanatiLimited && (summary ?? '').length > KANATI_MAX_TEXT_LENGTH;

  useEffect(() => {
    if (isKanati) {
      // Kanati tickets accept only a subset of file types; drop unsupported files already attached
      const files: File[] = getValues('attachments') ?? [];
      if (!files.every(isKanatiAttachmentAllowed)) {
        setValue('attachments', files.filter(isKanatiAttachmentAllowed));
      }
    }
  }, [isKanati, getValues, setValue]);

  const handleChange = (value: string) => {
    clearErrors('summary');
    setValue('summary', value, { shouldDirty: true });
  };

  //-----------------------------------------------------------------
  // set organization select to selected organization
  useEffect(() => {
    if (organizations.length > 0) {
      setValue('organization_id', selectedOrgId);
    }
  }, [selectedOrgId, organizations]);

  // Preserve template fields during initial organization selection.
  const prevOrgRef = useRef(formOrgId);
  useEffect(() => {
    if (prevOrgRef.current !== formOrgId) {
      setValue('tags', []);
      if (prevOrgRef.current > 0 && formOrgId > 0) {
        setValue('ticket_category', '', { shouldValidate: true });
        setValue('attachments', []);
      }
    }
    prevOrgRef.current = formOrgId;
  }, [formOrgId, setValue]);

  // business logic to conditionally set title input (category change)
  useEffect(() => {
    if (ticketCategory === CATEGORY_ID.LOG_RESTORE_EXPORT) {
      setValue('title', 'Log Restore/Export');
    }
  }, [ticketCategory]);

  // business logic to conditionally set title input (radio button change)
  useEffect(() => {
    if (ticketCategory === CATEGORY_ID.LOG_RESTORE_EXPORT) {
      if (logsAction === LOGS_ACTION_ID.EXPORT) {
        setValue('title', 'Log Restore/Export - Log Export Request');
      }
      if (logsAction === LOGS_ACTION_ID.RESTORE) {
        setValue('title', 'Log Restore/Export - Log Restore Request');
      }
    }
  }, [logsAction, ticketCategory]);

  return (
    <>
      <Stack direction={isLayoutMobile ? 'column' : 'row'} spacing={2}>
        {organizations.length > 1 && (
          <Stack className={classes.ticketCreateDialogFormElement}>
            <InputLabel htmlFor="organization-select">
              Organization *
            </InputLabel>
            <Controller
              control={control}
              name="organization_id"
              rules={{
                required: true
              }}
              render={({ field, formState }) => (
                <FormControl fullWidth>
                  <Select
                    id="organization-select"
                    MenuProps={{
                      style: { maxHeight: 450 }
                    }}
                    error={!!formState.errors.organization_id}
                    {...field}
                  >
                    {organizations?.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Stack>
        )}
        <ServiceFormCategoryField />
        <ServiceFormSubCategoryField />
      </Stack>
      {isKanati && KANATI_CATEGORY_INFO_MESSAGES[ticketCategory] && (
        <Alert severity="info">
          <AlertTitle>
            {KANATI_CATEGORY_INFO_MESSAGES[ticketCategory].title}
          </AlertTitle>
          {KANATI_CATEGORY_INFO_MESSAGES[ticketCategory].description}
          {ticketCategory === CATEGORY_ID.KANATI_ADVISORY && (
            <Box sx={{ mt: 2 }}>{KANATI_ADVISORY_DISCLAIMER}</Box>
          )}
        </Alert>
      )}
      <div>
        <InputLabel htmlFor="title-input">Title *</InputLabel>
        <Controller
          control={control}
          name="title"
          rules={{
            required: true
          }}
          render={({ field, formState }) => (
            <FormControl fullWidth>
              <TextField
                id="title-input"
                error={!!formState.errors.title}
                {...field}
              />
            </FormControl>
          )}
        />
      </div>
      <div>
        <InputLabel htmlFor="ticket-message-input">Summary *</InputLabel>
        <Controller
          control={control}
          name="summary"
          rules={{
            required: true,
            validate: (value) =>
              !isKanatiLimited ||
              (value ?? '').length <= KANATI_MAX_TEXT_LENGTH ||
              KANATI_TEXT_TOO_LONG_MESSAGE
          }}
          render={({ fieldState }) => (
            <Box>
              <Box
                className={classes.summaryErrorBorder}
                sx={
                  isSummaryTooLong ? { borderColor: 'error.main' } : undefined
                }
              >
                <MDXEditor
                  className={`${themeMode}-theme dark-editor`}
                  contentEditableClassName="cm-richView"
                  markdown={summary}
                  plugins={MDX_EDITOR_CONFIG}
                  suppressHtmlProcessing
                  onChange={(value) => {
                    handleChange(value);
                  }}
                />
              </Box>
              {isKanatiLimited && (
                <FormHelperText
                  error={isSummaryTooLong || !!fieldState.error?.message}
                >
                  {(summary ?? '').length}/{KANATI_MAX_TEXT_LENGTH}
                </FormHelperText>
              )}
            </Box>
          )}
        />
      </div>
      {settings?.tags && settings.tags.length > 0 && (
        <div>
          <InputLabel htmlFor="tags-input">Tags</InputLabel>
          <Controller
            control={control}
            name="tags"
            render={({ field }) => (
              <Autocomplete
                id="tags-input"
                multiple
                options={settings.tags || []}
                value={field.value || []}
                onChange={(_, newValue) => field.onChange(newValue)}
                renderValue={(value, getTagProps) =>
                  value.map((option: string, index: number) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        {...tagProps}
                        label={option}
                        size="small"
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select tags"
                    size="small"
                  />
                )}
              />
            )}
          />
        </div>
      )}
      <FileUploader key={formOrgId} {...getAttachmentUploaderProps(isKanati)} />
    </>
  );
};

export default ServiceForm;
